import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/lib/cart";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (!active) return;
        setInfo(r.data);
        if (r.data.payment_status === "paid") {
          setState("paid");
          clear();
          return;
        }
        if (r.data.status === "expired") {
          setState("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="max-w-xl mx-auto px-6 py-20">
      <div className="bg-sand/92 backdrop-blur-sm border border-tan/50 p-10 float-card text-center">
        {state === "checking" && (
          <>
            <Loader2 size={42} className="mx-auto text-copper animate-spin" />
            <h1 className="font-serif text-3xl text-navy mt-5">Confirming your payment…</h1>
            <p className="text-charcoal/75 mt-2">Please hold on, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-[#2f6f3a]" />
            <h1 data-testid="success-heading" className="font-serif text-4xl text-navy mt-5">Payment received</h1>
            <p className="text-charcoal/80 mt-3">
              Thank you! Your order {info?.order_number ? <strong>{info.order_number}</strong> : ""} is confirmed and paid.
              We'll be in touch with pickup / shipping details.
            </p>
            <Link to="/account" data-testid="success-account-btn" className="mt-7 inline-block bg-navy text-white px-8 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-charcoal">View my orders</Link>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle size={46} className="mx-auto text-texasred" />
            <h1 className="font-serif text-3xl text-navy mt-5">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="text-charcoal/80 mt-3">
              {state === "timeout"
                ? "We couldn't confirm payment yet. If you completed checkout, your order will update shortly — check My Orders."
                : "Your payment was cancelled or could not be verified. You can try again from your cart."}
            </p>
            <div className="mt-7 flex justify-center gap-3">
              <Link to="/account" className="border border-navy text-navy px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-navy hover:text-white">My Orders</Link>
              <Link to="/cart" className="bg-navy text-white px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-charcoal">Back to Cart</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
